import React, { useMemo } from "react";

/*
	Grafica de barras del tablero, en HTML puro.

	Antes era un SVG con viewBox, y ese fue el error: el SVG se escala al ancho
	del contenedor, asi que su texto se dibujaba a un tamano y luego se reducia.
	El navegador rasteriza distinto un texto escalado que uno a tamano nativo, y
	el espaciado entre letras terminaba viendose distinto al del resto de la
	pantalla aunque fuera la misma fuente y el mismo tamano nominal.

	Con divs, cada texto es texto normal a su tamano real y cae exactamente
	igual que el resto de la interfaz. Las barras son cajas con alto en pixeles.
*/

export interface BarDatum {
	label: string;
	total: number;
}

interface Props {
	titulo: string;
	datos: BarDatum[];
	/* "normal" pinta naranja; "urgente" pinta oro */
	tono: "normal" | "urgente";
	rotuloHoy: string;
	rangos: string[];
	rangoActivo: string;
	onRango: (r: string) => void;
	cargando: boolean;
	idSelect: string;
}

/* alto del area de barras, en pixeles reales */
const ALTO = 118;
/* una barra en 0 conserva un hilo visible para que se vea que el dia existe */
const ALTO_CERO = 3;

const BarChart = ({
	titulo, datos, tono, rotuloHoy,
	rangos, rangoActivo, onRango, cargando, idSelect,
}: Props) => {
	const total = useMemo(() => datos.reduce((a, d) => a + d.total, 0), [datos]);

	/* Mas de 8 barras no caben con su fecha completa en un celular: alli se ve solo el dia, una si y una no (CSS). */
	const denso = datos.length > 8;
	const rotuloHoyCorto = rotuloHoy.replace("semana", "sem.");

	const barras = useMemo(() => {
		if (datos.length === 0) return [];
		const max = Math.max(...datos.map((d) => d.total), 1);
		return datos.map((d, i) => ({
			...d,
			alto: d.total === 0 ? ALTO_CERO : Math.max(Math.round((d.total / max) * ALTO), 6),
			esUltimo: i === datos.length - 1,
		}));
	}, [datos]);

	return (
		<div className="wt-chart">
			<header>
				<h3>{titulo}</h3>
				<span className="wt-total">
					<b className={cargando ? "wt-skeleton" : undefined}>{cargando ? "00" : total}</b>
					en el periodo
				</span>
				{/* Select invisible encima de un texto con borde y flecha: en iPhone un select con flecha propia sale con dos
				    flechas aunque lleve appearance:none, y con letra de menos de 16px iOS acerca la pagina al tocarlo
				    (mismo patron que SelPeriodo del CRM). */}
				<span className="wt-range">
					<span className="wt-range-t" aria-hidden="true">{rangoActivo}</span>
					<select
						id={idSelect}
						value={rangoActivo}
						onChange={(e) => onRango(e.target.value)}
						disabled={cargando}
						aria-label={`Rango de ${titulo.toLowerCase()}`}
					>
						{rangos.map((r) => (
							<option key={r} value={r}>{r}</option>
						))}
					</select>
					<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
						strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
				</span>
			</header>

			{barras.length === 0 ? (
				<div className="wt-vacio">{cargando ? "Cargando…" : "Sin movimientos en el periodo"}</div>
			) : (
				<div className="wt-plot" data-tono={tono} data-denso={denso ? "si" : undefined} role="img"
					aria-label={`${titulo}: ${datos.map((d) => `${d.label} ${d.total}`).join(", ")}`}>
					{barras.map((b) => (
						<div
							key={b.label}
							className="wt-col"
							data-hoy={b.esUltimo ? "si" : undefined}
						>
							<span className="wt-val">{b.total}</span>
							<div className="wt-bar" style={{ height: `${b.alto}px` }} />
							<span className="wt-xlabel">
								{b.esUltimo ? (
									<>
										<span className="wt-largo">{rotuloHoy}</span>
										<span className="wt-corto">{rotuloHoyCorto}</span>
									</>
								) : (
									<>
										{b.label.split(" ")[0]}
										<span className="wt-mes">{b.label.slice(b.label.split(" ")[0].length)}</span>
									</>
								)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default BarChart;
